import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { AuthContext } from 'app/um/lib/auth-context';
import { guid } from 'app/shared/lib/guid';
import { debounce } from 'app/shared/lib/decorators';
import { IqcSummaryDiagnosesGrid } from 'app/ext/iqc/iqc-summary-diagnoses-grid';
import { IqcSummaryServicesGrid } from 'app/ext/iqc/iqc-summary-services-grid';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService } from 'app/um/services/auth-service';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class IqcSummary extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable
    protected auth?: UM.Auth;
    protected diagnosesMedicalReviews: UM.AuthMedicalReview[] = [];
    protected serviceMedicalReviews: UM.AuthMedicalReview[] = [];
    protected dentalServiceMedicalReviews: UM.AuthMedicalReview[] = [];
    protected hasConsolidatedViewData = false;

    protected ignoreExpandCollapse = false;
    protected allExpanded = true;
    protected allCollapsed = false;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $collapse!: ICollapse;
    protected $diagnosesGrid!: IqcSummaryDiagnosesGrid;
    protected $servicesGrid!: IqcSummaryServicesGrid;
    protected $dentalServicesGrid!: IqcSummaryServicesGrid;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(protected context: AuthContext,
        private readonly authService: AuthService,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.loadAuthMedicalReviews();
    }

    ////****************** Events ***************************************************////
    @debounce()
    public expandAll(event?: Event): void {
        // remove focus from button
        if(event?.target instanceof HTMLElement) event.target.blur();

        try {
            this.ignoreExpandCollapse = true;
            if(!event) {
                // if not triggered by a user event, open all but the last section (Consolidated View)
                const count = this.$collapse.getCounts().total;
                for(let i = 0; i < count - 1; i++) this.$collapse.open(i);
            } else {
                this.$collapse.openAll();
            }

            this.allExpanded = true;
        } finally {
            this.ignoreExpandCollapse = false;
        }

        if(this.$diagnosesGrid) this.$diagnosesGrid.expandAll();
        if(this.$servicesGrid) this.$servicesGrid.expandAll();
        if(this.$dentalServicesGrid) this.$dentalServicesGrid.expandAll();
    }

    protected collapseAll(event: Event): void {
        // remove focus from button
        if(event.target instanceof HTMLElement) event.target.blur();
        if(this.$diagnosesGrid) this.$diagnosesGrid.collapseAll();
        if(this.$servicesGrid) this.$servicesGrid.collapseAll();
        if(this.$dentalServicesGrid) this.$dentalServicesGrid.collapseAll();

        try {
            this.ignoreExpandCollapse = true;
            this.$collapse.closeAll();
            this.allCollapsed = true;
        } finally {
            this.ignoreExpandCollapse = false;
        }
    }

    protected updateCollapsed(): void {
        if(!this.ignoreExpandCollapse) {
            const counts = this.$collapse.getCounts();
            this.allCollapsed = counts.closed === counts.total;
            this.allExpanded = counts.open === counts.total;
        }
    }

    ////****************** Functions ************************************************////


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAuthMedicalReviews(): Promise<void> {
        if(!this.auth?.id) return;

        const sorting: Sorting = new Sorting();
        sorting.add({
            reference: 'reviewCreatedDateTime',
            direction: SortDirection.Descending
        });

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            sorting
        };

        const includes = [
            'diagnosis',
            'history.medicalReviews.content',
            'service',
            'service.definition',
            'service.definition.type'
        ];

        const fields = [
            'history.medicalReviews.content.data',
            'service.definition.type.code',
        ];

        try {
            this.state.beginLoad();
            const response = await this.authService.loadAuthMedicalReviews(this.auth.id, options, includes, fields);
            const medicalReviews = response.results;
            for(const medicalReview of medicalReviews) {
                if(!!medicalReview.diagnosis?.id) {
                    this.diagnosesMedicalReviews.push(medicalReview);
                } else if(!!medicalReview.service?.id) {
                    if(medicalReview.service.definition?.type?.code !== 'CDT') this.serviceMedicalReviews.push(medicalReview);
                    else this.dentalServiceMedicalReviews.push(medicalReview);
                }
            }

            this.hasConsolidatedViewData = this.hasMedicalReviewContent(this.diagnosesMedicalReviews)
                || this.hasMedicalReviewContent(this.serviceMedicalReviews)
                || this.hasMedicalReviewContent(this.dentalServiceMedicalReviews);
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    private hasMedicalReviewContent(medicalReviews: UM.AuthMedicalReview[]): boolean {
        return medicalReviews.some(medicalReview =>
            !!medicalReview.history?.medicalReviews?.some(review => !!review.content?.data));
    }
}
